import { useCallback, useEffect, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useApiClient } from "../../api/ApiClientContext";
import type { Sharing } from "../../api/types";
import { ApiError } from "../../api/types";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { useRowMenu } from "../../hooks/useRowMenu";
import { openAction, type RowAction } from "../../utils/explorerActions";
import { closeSharingAction } from "../../utils/sharingActions";

/** "N+" when the server's walk hit its cap (countsTruncated), otherwise the exact count. */
function countLabel(count: number, truncated: boolean): string {
  return truncated ? `${count}+` : `${count}`;
}

interface CreateSharingDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (sharing: Sharing) => void;
}

function CreateSharingDialog({ open, onClose, onCreated }: CreateSharingDialogProps) {
  const apiClient = useApiClient();
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function resetAndClose() {
    setDisplayName("");
    setError(null);
    onClose();
  }

  const displayNameValid = displayName.trim().length > 0 && displayName.trim().length <= 64;

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);

    if (!displayNameValid) {
      setError("Enter a partner name between 1 and 64 characters.");
      return;
    }

    setSubmitting(true);
    try {
      // No code: the server generates one at random (manager decision,
      // 2026-09-11). The server also normalises the name; the uppercase
      // transform below is a display convenience, not the source of truth.
      const sharing = await apiClient.createSharing({ displayName: displayName.trim() });
      onCreated(sharing);
      resetAndClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not create the sharing.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onClose={resetAndClose} maxWidth="xs" fullWidth aria-labelledby="create-sharing-title">
      <Box component="form" onSubmit={handleSubmit} noValidate>
        <DialogTitle id="create-sharing-title">Create sharing</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            <TextField
              label="Partner name"
              fullWidth
              autoFocus
              value={displayName}
              // Transforms the value itself to uppercase as the user
              // types, not just a CSS text-transform: what is shown is
              // what gets submitted.
              onChange={(event) => setDisplayName(event.target.value.toUpperCase())}
              slotProps={{ htmlInput: { maxLength: 64 } }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={resetAndClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            {submitting ? "Creating..." : "Create sharing"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

export function StaffSharingsPage() {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const location = useLocation();
  const rowMenu = useRowMenu();
  const [sharings, setSharings] = useState<Sharing[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [closeTarget, setCloseTarget] = useState<Sharing | null>(null);
  const [closeError, setCloseError] = useState<string | null>(null);

  // Manual refresh (after creating or closing a sharing): a plain event
  // handler, not an effect, so setting loading synchronously here is fine.
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setSharings(await apiClient.listSharings());
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not load sharings.");
    } finally {
      setLoading(false);
    }
  }, [apiClient]);

  // Initial load on mount: `loading` already starts true, and every
  // setState below only runs after `await apiClient.listSharings()`
  // resolves, so none is reachable synchronously from this effect body
  // (react-hooks/set-state-in-effect), same pattern as AuthContext's
  // initial session load.
  useEffect(() => {
    let ignore = false;
    void (async () => {
      try {
        const result = await apiClient.listSharings();
        if (!ignore) {
          setSharings(result);
          setError(null);
        }
      } catch (caught) {
        if (!ignore) {
          setError(caught instanceof ApiError ? caught.message : "Could not load sharings.");
        }
      } finally {
        if (!ignore) {
          setLoading(false);
        }
      }
    })();
    return () => {
      ignore = true;
    };
  }, [apiClient]);

  /** The list's own URL (path + any query), handed to the detail page as router state so its back-navigation (breadcrumb, back button) returns here, preserving whatever state the list URL carries (manager decision, 2026-09-11). */
  function backToListState() {
    return { backTo: `${location.pathname}${location.search}` };
  }

  function openSharing(sharing: Sharing) {
    navigate(`/staff/sharings/${sharing.code}`, { state: backToListState() });
  }

  /** Open on every row; Close sharing only where it still applies (an already-closed sharing offers no Close action). */
  function buildSharingActions(sharing: Sharing): RowAction[] {
    const actions: RowAction[] = [openAction(() => openSharing(sharing))];
    if (!sharing.closed) {
      actions.push(closeSharingAction(() => setCloseTarget(sharing)));
    }
    return actions;
  }

  function handleRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, sharing: Sharing) {
    rowMenu.handleKeyDown(event, sharing.code, buildSharingActions(sharing));
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openSharing(sharing);
    }
  }

  async function handleCloseConfirmed() {
    if (!closeTarget) {
      return;
    }
    setCloseError(null);
    try {
      await apiClient.closeSharing(closeTarget.code);
      setCloseTarget(null);
      await load();
    } catch (caught) {
      setCloseError(caught instanceof ApiError ? caught.message : "Could not close the sharing.");
    }
  }

  const menuSharing = rowMenu.menu ? sharings?.find((sharing) => sharing.code === rowMenu.menu!.rowKey) : undefined;
  const menuActions = menuSharing ? buildSharingActions(menuSharing) : [];

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Sharings
        </Typography>
        <Button variant="contained" onClick={() => setCreateOpen(true)}>
          Create sharing
        </Button>
      </Stack>

      <Paper variant="outlined">
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Stack sx={{ alignItems: "center", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} aria-label="Loading sharings" />
          </Stack>
        )}

        {!loading && sharings && sharings.length === 0 && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography color="text.secondary">No sharings yet. Create one to get started.</Typography>
          </Box>
        )}

        {!loading && sharings && sharings.length > 0 && (
          <Table aria-label="Sharings">
            <TableHead>
              <TableRow>
                <TableCell>Partner</TableCell>
                <TableCell align="right">Members</TableCell>
                <TableCell align="right">Files</TableCell>
                <TableCell align="right">Folders</TableCell>
                <TableCell>IP allowlist</TableCell>
                <TableCell>Status</TableCell>
                <TableCell padding="checkbox" />
              </TableRow>
            </TableHead>
            <TableBody>
              {sharings.map((sharing) => {
                const actions = buildSharingActions(sharing);
                return (
                  <TableRow
                    key={sharing.code}
                    hover
                    tabIndex={0}
                    onClick={() => openSharing(sharing)}
                    onKeyDown={(event) => handleRowKeyDown(event, sharing)}
                    onContextMenu={(event) => rowMenu.handleContextMenu(event, sharing.code, actions)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Link
                        component={RouterLink}
                        to={`/staff/sharings/${sharing.code}`}
                        state={backToListState()}
                        underline="hover"
                        onClick={(event) => event.stopPropagation()}
                      >
                        {sharing.displayName}
                      </Link>
                    </TableCell>
                    <TableCell align="right">{sharing.memberCount}</TableCell>
                    <TableCell align="right">{countLabel(sharing.fileCount, sharing.countsTruncated)}</TableCell>
                    <TableCell align="right">{countLabel(sharing.folderCount, sharing.countsTruncated)}</TableCell>
                    <TableCell>
                      {/* Defensive: the BFF sends a JSON null, not [], for
                          an unrestricted sharing's nil Go slice. */}
                      {(sharing.allowedIP ?? []).length === 0 ? "No" : "Yes"}
                    </TableCell>
                    <TableCell>
                      {sharing.closed ? (
                        <Chip size="small" label="Closed" />
                      ) : (
                        <Chip size="small" color="success" label="Open" />
                      )}
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton
                        aria-label={`More actions for ${sharing.displayName}`}
                        size="small"
                        onClick={(event) => {
                          event.stopPropagation();
                          rowMenu.openAtElement(sharing.code, event.currentTarget);
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>

            {rowMenu.menu && menuSharing && (
              <Menu
                open
                onClose={rowMenu.close}
                anchorEl={rowMenu.menu.anchorEl ?? undefined}
                anchorReference={rowMenu.menu.anchorPosition ? "anchorPosition" : "anchorEl"}
                anchorPosition={rowMenu.menu.anchorPosition ?? undefined}
              >
                {/*
                  Close sharing (destructive): separated by a divider and
                  rendered in the error colour, as the last item (manager
                  decision, 2026-09-11). Flattened, not nested in a
                  wrapper element, since MUI's Menu expects Divider/
                  MenuItem as its direct children for keyboard navigation.
                */}
                {menuActions.flatMap((action) => [
                  action.type === "close" ? <Divider key={`${action.key}-divider`} /> : null,
                  <MenuItem
                    key={action.key}
                    onClick={(event) => {
                      event.stopPropagation();
                      rowMenu.close();
                      action.onSelect();
                    }}
                    sx={action.type === "close" ? { color: "error.main" } : undefined}
                  >
                    {action.label}
                  </MenuItem>,
                ])}
              </Menu>
            )}
          </Table>
        )}
      </Paper>

      <CreateSharingDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => void load()}
      />

      <ConfirmDialog
        open={closeTarget !== null}
        title="Close sharing"
        description={
          closeTarget && (
            <>
              This removes every member of <strong>{closeTarget.displayName}</strong>. Files are
              kept.
            </>
          )
        }
        confirmLabel="Close sharing"
        confirmationText="DELETE"
        uppercaseInput
        destructive
        error={closeError}
        onConfirm={() => handleCloseConfirmed()}
        onClose={() => {
          setCloseTarget(null);
          setCloseError(null);
        }}
      />
    </Box>
  );
}
